import { useMemo, useState } from 'react';
import { Card, SectionHeader } from '@/components/ui/card';
import { Pill } from '@/components/ui/pill';
import { Input } from '@/components/ui/field';
import { cn } from '@/lib/cn';
import { assetUrl } from '@/lib/asset';
import reference from './reference.generated.json';

type Reference = typeof reference;
type Operation = Reference['groups'][number]['operations'][number];
type Schema = Reference['schemas'][number];

const methodTone: Record<string, string> = {
  GET: 'bg-[color:var(--ts-pill-info-bg)] text-[color:var(--ts-pill-info-text)]',
  POST: 'bg-[color:var(--ts-pill-success-bg)] text-[color:var(--ts-pill-success-text)]',
  PATCH: 'bg-[color:var(--ts-pill-warning-bg)] text-[color:var(--ts-pill-warning-text)]',
  DELETE: 'bg-[color:var(--ts-pill-danger-bg)] text-[color:var(--ts-pill-danger-text)]',
};

/** Every customer-facing endpoint, generated from the contract. Staff endpoints are left out. */
export function ApiReference() {
  const [term, setTerm] = useState('');
  const needle = term.trim().toLowerCase();

  const groups = useMemo(
    () =>
      reference.groups
        .map((group) => ({
          ...group,
          operations: group.operations.filter(
            (op) =>
              needle === '' ||
              op.path.toLowerCase().includes(needle) ||
              op.summary.toLowerCase().includes(needle) ||
              op.method.toLowerCase() === needle,
          ),
        }))
        .filter((group) => group.operations.length > 0),
    [needle],
  );

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <SectionHeader
          title="Endpoints"
          description={`Every endpoint a customer key can call, from the contract, version ${reference.version}.`}
          actions={
            <a
              href={assetUrl("openapi.yaml")}
              download="tab-service-efiling-openapi.yaml"
              className="text-base font-medium text-link hover:underline"
            >
              Download OpenAPI 3.1
            </a>
          }
        />
        <div className="max-w-sm">
          <Input value={term} onChange={(event) => setTerm(event.target.value)} placeholder="Filter by path, method or words" aria-label="Filter endpoints" />
        </div>
      </Card>

      {groups.map((group) => (
        <Card key={group.tag} id={`ref-${group.tag.toLowerCase()}`}>
          <SectionHeader title={group.tag} description={group.description || undefined} />
          <div className="flex flex-col divide-y divide-[color:var(--ts-border-soft)]">
            {group.operations.map((op) => (
              <OperationRow key={op.id} op={op} />
            ))}
          </div>
        </Card>
      ))}

      <Card id="ref-schemas">
        <SectionHeader title="Objects" description="The request and response objects the endpoints above use." />
        <div className="flex flex-col divide-y divide-[color:var(--ts-border-soft)]">
          {reference.schemas.map((schema) => (
            <SchemaRow key={schema.name} schema={schema} />
          ))}
        </div>
      </Card>
    </div>
  );
}

function OperationRow({ op }: { op: Operation }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-3">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-3 rounded-md text-left transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]"
      >
        <span className={cn('w-16 rounded-md px-2 py-0.5 text-center font-mono text-xs font-semibold', methodTone[op.method] ?? '')}>{op.method}</span>
        <code className="font-mono text-sm text-gray-3">{op.path}</code>
        <span className="text-base text-muted-foreground">{op.summary}</span>
        {op.anonymous ? <Pill tone="neutral">No key needed</Pill> : null}
      </button>
      {open ? (
        <div className="mt-3 flex flex-col gap-4 pl-2 text-base text-gray-3">
          {op.description ? <p className="whitespace-pre-line text-muted-foreground">{op.description}</p> : null}
          {op.params.length > 0 ? (
            <div>
              <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Parameters</p>
              <ul className="mt-2 flex flex-col gap-1">
                {op.params.map((param) => (
                  <li key={`${param.in}-${param.name}`} className="flex flex-wrap gap-2">
                    <code className="font-mono text-sm">{param.name}</code>
                    <span className="text-muted-foreground">
                      {param.in}
                      {param.required ? ', required' : ''} · {param.type}
                    </span>
                    {param.description ? <span className="text-muted-foreground">— {param.description}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {op.requestBody ? (
            <div>
              <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Request body</p>
              <p className="mt-1">
                {'oneOf' in op.requestBody && op.requestBody.oneOf
                  ? op.requestBody.oneOf.map((name, index) => (
                      <span key={name}>
                        {index > 0 ? ' or ' : ''}
                        <SchemaLink name={name} />
                      </span>
                    ))
                  : 'name' in op.requestBody ? <SchemaLink name={op.requestBody.name} /> : null}
                {op.requestBody.required ? <span className="text-muted-foreground"> (required)</span> : null}
              </p>
            </div>
          ) : null}
          <div>
            <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">Responses</p>
            <ul className="mt-2 flex flex-col gap-1">
              {op.responses.map((response) => (
                <li key={response.code} className="flex flex-wrap gap-2">
                  <code className="font-mono text-sm">{response.code}</code>
                  <span className="text-muted-foreground">{response.description}</span>
                  {'name' in response && response.name ? <SchemaLink name={response.name} /> : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SchemaLink({ name }: { name: string }) {
  const known = reference.schemas.some((schema) => schema.name === name);
  if (!known) return <code className="font-mono text-sm">{name}</code>;
  return (
    <a href={`#schema-${name}`} className="font-mono text-sm text-link hover:underline">
      {name}
    </a>
  );
}

function SchemaRow({ schema }: { schema: Schema }) {
  return (
    <div id={`schema-${schema.name}`} className="py-3">
      <p className="font-mono text-sm font-semibold text-gray-3">{schema.name}</p>
      {schema.description ? <p className="mt-1 whitespace-pre-line text-base text-muted-foreground">{schema.description}</p> : null}
      {schema.enum ? (
        <p className="mt-2 text-base text-gray-3">
          One of: <code className="font-mono text-sm">{schema.enum.join(', ')}</code>
        </p>
      ) : null}
      {schema.fields.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {schema.fields.map((field) => (
            <li key={field.name} className="flex flex-wrap gap-2 text-base">
              <code className="font-mono text-sm">{field.name}</code>
              <span className="text-muted-foreground">
                {field.type}
                {field.required ? ', required' : ''}
              </span>
              {field.description ? <span className="text-muted-foreground">— {field.description}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
